/*
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2025, Zumper
 */

import { IS_PRODUCTION } from '@zcode/env'
import { useLocation } from 'react-router-dom'
import { APP_HOSTNAME } from '../../../settings'
import { IS_NOT_PRODUCTION } from '../../env'
import { localStorageSingleton } from '../../utils'
import {
  BANNER_SUFFIXES,
  CT,
  MT,
  MT_VALUE,
  type PADMAPPER_APPSFLYER_ANDROID_PID,
  type PADMAPPER_APPSFLYER_IOS_PID,
  PT,
  PT_VALUE,
  type ZUMPER_APPSFLYER_ANDROID_PID,
  type ZUMPER_APPSFLYER_GENERIC_PID,
  type ZUMPER_APPSFLYER_IOS_PID,
} from './constants'
import type { getUtmPageType } from './getUtmPageType'

const getBaseUrl = (pId: string) => {
  const subdomain = IS_PRODUCTION ? 'deeplinks' : 'deeplinks-test'
  const domainAndPath = pId.includes('zumper') ? 'zumper.com/7DdE' : 'padmapper.com/KYUN'
  return `https://${subdomain}.${domainAndPath}`
}

export const useConstructAppsFlyerLink = (
  cParam: ReturnType<typeof getUtmPageType>,
  pId:
    | typeof ZUMPER_APPSFLYER_GENERIC_PID
    | typeof ZUMPER_APPSFLYER_IOS_PID
    | typeof ZUMPER_APPSFLYER_ANDROID_PID
    | typeof PADMAPPER_APPSFLYER_IOS_PID
    | typeof PADMAPPER_APPSFLYER_ANDROID_PID,
  bannerSuffix: (typeof BANNER_SUFFIXES)[keyof typeof BANNER_SUFFIXES] = BANNER_SUFFIXES.IOS_MWEB_BANNER_SUFFIX
) => {
  const baseUrl = getBaseUrl(pId)
  const appsFlyerUrl = new URL(baseUrl)
  const appsFlyerParams = appsFlyerUrl.searchParams
  const location = useLocation()
  const deepLink = new URL(
    `https://${APP_HOSTNAME}${location.pathname}${location.search}${location.hash}`
  )

  const traffic: string | undefined = localStorageSingleton.getx('_traffic')
  const campaign: string | undefined = localStorageSingleton.getx('_campaign')
  const utm_campaign: string | undefined = localStorageSingleton.getx('utm_campaign')
  const utm_medium: string | undefined = localStorageSingleton.getx('utm_medium')

  let trafficParam: string
  if (traffic) {
    trafficParam = `${String(traffic)}-${bannerSuffix}`
  } else {
    trafficParam = bannerSuffix
  }
  deepLink.searchParams.set('T', trafficParam)
  if (campaign) {
    deepLink.searchParams.set('C', campaign)
  }

  const ctValue = `${campaign || ''},${traffic || ''}-${bannerSuffix}`

  appsFlyerParams.set('pid', pId)
  appsFlyerParams.set('c', cParam)
  appsFlyerParams.set('deep_link_value', deepLink.toString())
  appsFlyerParams.set(MT, MT_VALUE)
  appsFlyerParams.set(CT, ctValue)
  appsFlyerParams.set(PT, PT_VALUE)
  appsFlyerParams.set('utm_source', 'app_banner')
  appsFlyerParams.set('utm_campaign', utm_campaign ?? cParam)
  appsFlyerParams.set('utm_medium', utm_medium ?? 'link')

  return appsFlyerUrl.toString()
}
